// Programme API service utilities
import { APP_URL, getJWTHeaders, getJWTFormDataHeaders } from './Config.js';

const API_BASE_URL = `${APP_URL}/api/programmes`;

export const ProgrammeAPI = {
    // Get all programmes
    async getAllProgrammes() {
        try {
            const response = await fetch(`${API_BASE_URL}/all`, {
                method: 'GET',
                headers: getJWTHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.status === 'success' && result.data) {
                return result;
            } else {
                throw new Error(result.message || 'Failed to fetch programmes');
            }
        } catch (error) {
            console.error('Error fetching all programmes:', error);
            throw error; // Re-throw to let caller handle
        }
    },
    // Get active programmes only
    async getActiveProgrammes() {
        try {
            const response = await fetch(`${API_BASE_URL}/active`, {
                method: 'GET',
                headers: getJWTHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.status === 'success' && result.data) {
                return result;
            } else {
                throw new Error(result.message || 'Failed to fetch programmes');
            }
        } catch (error) {
            console.error('Error fetching active programmes:', error);
            throw error; // Re-throw to let caller handle
        }
    },
    // Get active programmes in a structured format
    async getActiveStructuredProgrammes() {
        try {
            const response = await fetch(`${API_BASE_URL}/active-structured`, {
                method: 'GET',
                headers: getJWTHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.status === 'success' && result.data) {
                return result;
            } else {
                throw new Error(result.message || 'Failed to fetch structured programmes');
            }
        } catch (error) {
            console.error('Error fetching active structured programmes:', error);
            throw error; // Re-throw to let caller handle
        }
    },
    // Get programme by ID
    async getProgrammeById(id) {
        try {
            const response = await fetch(`${API_BASE_URL}/${id}`, {
                method: 'GET',
                headers: getJWTHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.status === 'success' && result.data) {
                return result;
            } else {
                throw new Error(result.message || 'Failed to fetch programme');
            }
        } catch (error) {
            console.error('Error fetching programme by ID:', error);
            throw error; // Re-throw to let caller handle
        }
    },
    // Create new programme
    async createProgramme(programmeData, createdBy) {
        try {
            const params = new URLSearchParams();
            params.append('createdBy', createdBy);
            params.append('name', programmeData.name);
            if (programmeData.description) {
                params.append('description', programmeData.description);
            }

            const response = await fetch(`${API_BASE_URL}/create?${params.toString()}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
                },
                body: programmeData.programmeStructureJson
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.status === 'success') {
                return result;
            } else {
                throw new Error(result.message || 'Failed to create programme');
            }
        } catch (error) {
            console.error('Error creating programme:', error);
            throw error; // Re-throw to let caller handle
        }
    },
    // Update programme
    async updateProgramme(id, programmeData, updatedBy) {
        try {
            const params = new URLSearchParams();
            params.append('updatedBy', updatedBy);
            if (programmeData.name) {
                params.append('name', programmeData.name);
            }
            if (programmeData.description) {
                params.append('description', programmeData.description);
            }

            const response = await fetch(`${API_BASE_URL}/${id}?${params.toString()}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
                },
                body: programmeData.programmeStructureJson
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.status === 'success') {
                return result;
            } else {
                throw new Error(result.message || 'Failed to update programme');
            }
        } catch (error) {
            console.error('Error updating programme:', error);
            throw error; // Re-throw to let caller handle
        }
    },
    // Toggle programme status (enable/disable)
    async toggleProgrammeStatus(id, updatedBy) {
        try {
            const response = await fetch(`${API_BASE_URL}/${id}/toggle-status?updatedBy=${updatedBy}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.status === 'success') {
                return result;
            } else {
                throw new Error(result.message || 'Failed to toggle programme status');
            }
        } catch (error) {
            console.error('Error toggling programme status:', error);
            throw error; // Re-throw to let caller handle
        }
    },

    // Utility functions for working with programme structures
    parseProgrammeStructure(jsonString) {
        try {
            return JSON.parse(jsonString);
        } catch (error) {
            console.error('Error parsing programme structure:', error);
            return null;
        }
    }
};

export default ProgrammeAPI;
